/**
 * MorphParticlePage — one MorphParticle of wave-engine that morphs on its own from shape to shape of the
 * catalog (./morphShapes): size, depth, trapezoid, base form and roundness all move together, the way morphTo
 * does it between two layouts. Between two base forms (cube → dodecahedron) it goes through the sphere, where
 * every base looks the same, and swaps the geometry there. „Rundung" sets the roundness of the current shape.
 * Drag to orbit.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import gsap from 'gsap';
import * as THREE from 'three';
import { createMorphGeometry, MORPH_INSTANCE_ATTRIBUTES } from '@arkturian/wave-engine/core/MorphGeometry';
import { MORPH_BASES, type MorphBase } from '@arkturian/wave-engine/core/MorphBase';
import { createUniforms, applyShaderToMaterial } from '@arkturian/wave-engine/core/MorphShader';
import { morphTo } from '@arkturian/wave-engine/core/LayoutWriter';
import { SHAPE_PRESETS, presetParams, presetResult, showcaseSegments } from './morphShapes';
import { RoundnessControl } from './RoundnessControl';

const SPEEDS = {
    langsam: { morph: 2.2, hold: 2.2 },
    normal: { morph: 1.4, hold: 1.3 },
    schnell: { morph: 0.8, hold: 0.6 },
} as const;
type Speed = keyof typeof SPEEDS;

const hueOf = (index: number) => new THREE.Color().setHSL(index / SHAPE_PRESETS.length, 0.6, 0.55);

/** Move the instance state (positions, sizes, colours …) from one base geometry to another. */
function copyInstanceState(from: THREE.BufferGeometry, to: THREE.BufferGeometry): void {
    for (const name of [...MORPH_INSTANCE_ATTRIBUTES, 'aColor']) {
        const a = to.getAttribute(name) as THREE.InstancedBufferAttribute;
        a.array.set((from.getAttribute(name) as THREE.InstancedBufferAttribute).array);
        a.needsUpdate = true;
    }
}

/** override = roundness from the slider (both edge groups), null = the preset's shape as it is. */
const Particle: React.FC<{ index: number; speed: Speed; override: number | null }> = ({ index, speed, override }) => {
    const meshRef = useRef<THREE.InstancedMesh>(null!);
    // one geometry per base form; the mesh shows one of them
    const geometries = useMemo(() => Object.fromEntries(MORPH_BASES.map(base =>
        [base, createMorphGeometry({ maxInstances: 1, base, segments: showcaseSegments(base, true) })])) as Record<MorphBase, THREE.BufferGeometry>, []);
    const uniforms = useMemo(() => {
        const u = createUniforms();
        u.uDepthDirection.value = 0; // symmetric depth: the particle stays centred while it morphs
        u.uColor1.value.copy(hueOf(0));
        return u;
    }, []);
    const material = useMemo(() => {
        const m = new THREE.MeshStandardMaterial({ roughness: 0.35, metalness: 0.15 });
        m.onBeforeCompile = (shader) => applyShaderToMaterial(shader, uniforms);
        return m;
    }, [uniforms]);
    const shown = useRef(-1);

    useEffect(() => {
        const mesh = meshRef.current;
        if (!mesh) return;
        const preset = SHAPE_PRESETS[index];
        const base = preset.base ?? 'cube';
        const shape = override !== null ? { roundness: override, hemi: preset.shape?.hemi } : preset.shape ?? {};
        const first = shown.current < 0;
        // a roundness change on the same shape is a short slide, a new shape the full morph
        const duration = first ? 0 : shown.current === index ? 0.3 : SPEEDS[speed].morph;
        shown.current = index;
        const target = [presetResult(preset)];
        if (first) mesh.geometry = geometries[base];
        if (mesh.geometry.userData.morphBase === base) {
            morphTo(mesh, uniforms, target, { duration, shape });
        } else {
            // through the sphere: there every base is the same, the swap is invisible
            morphTo(mesh, uniforms, target, {
                duration: duration / 2, shape: { roundness: 1 },
                onComplete: () => {
                    const next = geometries[base];
                    copyInstanceState(mesh.geometry, next);
                    mesh.geometry = next;
                    morphTo(mesh, uniforms, target, { duration: duration / 2, shape });
                },
            });
        }
        const to = hueOf(index);
        gsap.killTweensOf(uniforms.uColor1.value);
        gsap.to(uniforms.uColor1.value, { r: to.r, g: to.g, b: to.b, duration: Math.max(duration, 0.01), ease: 'power2.inOut' });
    }, [index, speed, override, uniforms, geometries]);

    useFrame((_, delta) => { uniforms.uTime.value += delta; });

    useEffect(() => () => { Object.values(geometries).forEach(g => g.dispose()); material.dispose(); }, [geometries, material]);

    return (
        <instancedMesh ref={meshRef} args={[geometries.cube, material, 1]} frustumCulled={false} scale={1.6} />
    );
};

const MorphParticlePage: React.FC = () => {
    const [index, setIndex] = useState(0);
    const [playing, setPlaying] = useState(true);
    const [speed, setSpeed] = useState<Speed>('normal');
    // roundness per shape set with the slider (index → 0–1); without one the preset's
    const [roundOverride, setRoundOverride] = useState<Record<number, number>>({});
    const n = SHAPE_PRESETS.length;

    // auto-play: one step per morph + hold
    useEffect(() => {
        if (!playing) return;
        const { morph, hold } = SPEEDS[speed];
        const id = window.setTimeout(() => setIndex(i => (i + 1) % n), (morph + hold) * 1000);
        return () => window.clearTimeout(id);
    }, [playing, speed, index, n]);

    const step = (d: number) => { setPlaying(false); setIndex(i => (i + d + n) % n); };
    const preset = SHAPE_PRESETS[index];
    const presetRound = preset.shape?.roundness ?? preset.shape?.roundnessXY ?? 0;
    const setRoundness = (v: number | null) => {
        setPlaying(false); // hold the shape while it is being set
        setRoundOverride(o => { const n = { ...o }; if (v === null) delete n[index]; else n[index] = v; return n; });
    };

    return (
        <div style={{ width: '100vw', height: '100vh', background: '#0f172a' }}>
            <Canvas camera={{ position: [1.6, 1.1, 3.4], fov: 45 }}>
                <color attach="background" args={['#0f172a']} />
                <ambientLight intensity={0.45} />
                <directionalLight position={[4, 6, 5]} intensity={1.6} />
                <directionalLight position={[-5, -2, -3]} intensity={0.35} color="#8fb4ff" />
                <hemisphereLight args={['#ffffff', '#334155', 0.3]} />
                <Particle index={index} speed={speed} override={roundOverride[index] ?? null} />
                <OrbitControls enableDamping dampingFactor={0.1} autoRotate autoRotateSpeed={0.8} />
            </Canvas>

            <div style={infoBox}>
                <div style={{ fontWeight: 700, fontSize: '16px', marginBottom: '4px' }}>{preset.name}</div>
                <div style={{ fontSize: '11px', color: '#94a3b8' }}>{presetParams(preset)}</div>
                <div style={{ fontSize: '10px', color: '#64748b', marginTop: '6px' }}>{index + 1} / {n} · Morph-Katalog</div>
            </div>

            <div style={bar}>
                <button onClick={() => step(-1)} style={navBtn} title="Vorige Form">◀</button>
                <button onClick={() => setPlaying(p => !p)} style={navBtn} title={playing ? 'Anhalten' : 'Abspielen'}>{playing ? '❚❚' : '►'}</button>
                <button onClick={() => step(1)} style={navBtn} title="Nächste Form">▶</button>
                <RoundnessControl value={roundOverride[index] ?? null} presetValue={presetRound} onChange={setRoundness} />
                <select value={speed} onChange={e => setSpeed(e.target.value as Speed)} style={select} title="Tempo">
                    {(Object.keys(SPEEDS) as Speed[]).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>
        </div>
    );
};

const infoBox: React.CSSProperties = {
    position: 'fixed', top: '56px', left: '10px', zIndex: 100,
    color: '#e2e8f0', fontFamily: 'monospace', fontSize: '13px',
    background: 'rgba(15,23,42,0.9)', padding: '10px 14px',
    borderRadius: '8px', backdropFilter: 'blur(8px)', maxWidth: 'calc(100vw - 52px)',
};

const bar: React.CSSProperties = {
    position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)',
    zIndex: 100, display: 'flex', gap: '10px', alignItems: 'center',
};

const navBtn: React.CSSProperties = {
    width: '48px', height: '48px', borderRadius: '50%',
    background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(99,102,241,0.4)',
    color: '#a78bfa', fontSize: '18px', cursor: 'pointer',
    backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const select: React.CSSProperties = {
    height: '48px', borderRadius: '24px', padding: '0 14px',
    background: 'rgba(15,23,42,0.85)', border: '1px solid rgba(99,102,241,0.4)',
    color: '#a78bfa', fontFamily: 'monospace', fontSize: '13px', cursor: 'pointer',
};

export default MorphParticlePage;
